import type { PlanLimitParams } from '../types/interfaces/account.interface'
import type { ValidationKey } from './validation-keys'

/**
 * Declares a code's params. Types only: the value is never read.
 */
const params = <P>(): P => undefined as P

export interface ApiErrorDefinition {
  /** The HTTP status `AppException` answers with. */
  status: number
  /** English, for logs, Swagger and developers. Never shown to a user. */
  message: string
  params?: unknown
}

/**
 * Every error code the API sends. It is the complete list, because `AppException` accepts only these
 * codes and it is the only thing in `apps/api` that sets one. See docs/api-errors.md.
 *
 * A code is also its translation key: every frontend locale is a mapped type over this object, so
 * adding a code fails the build until each locale translates it.
 *
 * Add a code for a failure the user can understand and act on. A failure only a developer can fix
 * stays a plain Nest exception: the frontend shows the generic text for its status.
 */
export const API_ERRORS = {
  'validation.failed': { status: 400, message: 'Request validation failed' },
  'reference.notFound': {
    status: 404,
    message: 'A referenced resource was not found',
    params: params<{ field: string }>(),
  },

  'auth.invalidCredentials': { status: 401, message: 'Invalid credentials' },
  'auth.emailInUse': { status: 409, message: 'Email in use' },
  'auth.emailDomainBlocked': { status: 400, message: 'Registration is not allowed with an email at this domain' },
  'account.wrongPassword': { status: 400, message: 'The current password is wrong' },
  'account.emailUnchanged': { status: 400, message: 'The new email is the current one' },
  'account.emailChangeLimit': {
    status: 429,
    message: 'No email changes left this year',
    params: params<{ nextAllowedAt: string }>(),
  },
  'account.emailChangeTooManyRequests': { status: 429, message: 'Too many email change requests today' },
  'account.emailChangeLinkInvalid': { status: 400, message: 'The email change link is expired, used or replaced' },

  'user.cannotBlock': { status: 409, message: 'Root accounts and your own account cannot be blocked' },

  'customer.blocked': { status: 403, message: 'The customer is blocked by the premise' },
  'customer.trialsExceeded': { status: 400, message: 'The customer has already taken more trials' },
  'customer.emailTaken': { status: 409, message: 'Another client of this premise has this e-mail' },

  'employee.alreadyEmployee': { status: 409, message: 'The user is already an employee' },
  'employee.emailInUse': { status: 409, message: 'An employee with this email already exists in the premise' },
  'employee.cannotDeleteOwner': { status: 403, message: 'The premise owner cannot be deleted' },
  'employeeInvoice.sourceRequired': {
    status: 400,
    message: 'An invoice item needs an activity or an event instance',
  },

  'event.started': { status: 403, message: 'The event has already started' },
  'event.full': { status: 403, message: 'The event is full' },
  'event.inactive': { status: 403, message: 'The event is not active' },
  'event.alreadyJoined': { status: 403, message: 'Already registered for the event' },
  /** A public sign-up refused by the premise's plan limit; no plan or usage for the visitor (PublicPlanLimitService). */
  'event.signUpUnavailable': { status: 402, message: 'The premise cannot take this sign-up right now' },
  /** Editing, locking, adding to or signing up for an event the premise called off (`cancelEvent`). */
  'event.cancelled': { status: 409, message: 'The event is cancelled' },
  'event.ended': { status: 409, message: 'The event has ended and can no longer be edited' },
  'event.ongoingWithParticipants': {
    status: 409,
    message: 'The event is in progress and has participants, it can no longer be edited',
  },
  'event.timeLocked': {
    status: 409,
    message: 'The event has participants, its time cannot be changed',
    params: params<{ participants: number }>(),
  },
  /** A series edit of an event that belongs to no series (`EventService.editEventSeries`). */
  'event.notRepeating': { status: 409, message: 'The event is not part of a series' },
  /** "Pridať do série" on a termín that already belongs to one (`seriesJoinRefusal`). */
  'event.alreadyInSeries': { status: 409, message: 'The event is already part of a series' },
  /** "Pridať do série" with a series whose activity is not the event's. */
  'event.activityMismatch': { status: 409, message: 'The series has a different activity than the event' },
  /** "Pridať do série" with a series that has no upcoming termín left. */
  'event.seriesEnded': { status: 409, message: 'The series has no upcoming events left' },
  'eventParticipation.eventEnded': {
    status: 409,
    message: 'The event has ended, a participant can no longer be removed',
  },
  'eventParticipation.eventNotStarted': {
    status: 409,
    message: 'Attendance can be recorded only once the event has started',
  },
  'eventParticipation.eventStarted': {
    status: 409,
    message: 'The event has started, a participation can no longer be cancelled',
  },
  'eventParticipation.cancelled': { status: 409, message: 'The participation is cancelled' },
  'eventParticipation.cancel.notAllowed': { status: 403, message: 'The participation cannot be cancelled' },
  'eventParticipation.cancel.deadlinePassed': { status: 403, message: 'The cancellation deadline has passed' },
  'event.deleteUsed': {
    status: 403,
    message: 'An event that started or has participants can be deleted only with event.purge:entity; cancel instead',
  },

  'reservation.slotUnavailable': { status: 400, message: 'The slot is not available' },
  'reservation.beyondHorizon': {
    status: 400,
    message: 'The slot is beyond the booking horizon',
    params: params<{ months: number }>(),
  },
  'reservation.cancel.notAllowed': { status: 403, message: 'The reservation cannot be cancelled' },
  'reservation.cancel.deadlinePassed': { status: 403, message: 'The cancellation deadline has passed' },
  'reservation.started': { status: 409, message: 'A reservation cannot be changed once it has started' },
  'reservation.cancelled': { status: 409, message: 'The reservation is cancelled' },
  /** A public booking refused by the premise's plan limit; no plan or usage for the visitor (PublicPlanLimitService). */
  'reservation.unavailable': { status: 402, message: 'The premise cannot take this booking right now' },
  'reservation.deleteUsed': {
    status: 403,
    message:
      'A reservation that started, was cancelled or has a customer can be deleted only with reservation.purge:entity',
  },

  'billing.disabled': { status: 409, message: 'Billing is disabled for this premise' },
  'billing.contactSales': { status: 409, message: 'The custom plan is arranged individually' },
  'billing.priceNotConfigured': {
    status: 409,
    message: 'The item cannot be purchased yet',
    params: params<{ itemName: string }>(),
  },
  'billing.addonRequiresPlan': { status: 409, message: 'Add-ons require a paid plan' },
  'sms.creditsInsufficient': {
    status: 409,
    message: 'Insufficient SMS credits',
    params: params<{ balance: number; requested: number }>(),
  },

  'image.unsupportedFormat': { status: 415, message: 'Only JPEG, PNG and WebP images are accepted' },
  'image.tooLarge': {
    status: 413,
    message: 'The image file is too large',
    params: params<{ maxMegabytes: number }>(),
  },
  'image.tooManyPixels': {
    status: 413,
    message: 'The image has too many pixels',
    params: params<{ maxMegapixels: number }>(),
  },
  'image.tooSmall': { status: 400, message: 'The image is too small', params: params<{ minWidth: number }>() },
  'image.galleryFull': { status: 409, message: 'The gallery is full', params: params<{ limit: number }>() },

  'plan.limit.count': { status: 402, message: 'Plan limit reached', params: params<PlanLimitParams>() },
  'plan.limit.quota': { status: 402, message: 'Plan quota exhausted', params: params<PlanLimitParams>() },
  'plan.limit.feature': { status: 402, message: 'Feature not in plan', params: params<PlanLimitParams>() },
} as const satisfies Record<string, ApiErrorDefinition>

export type ApiErrorCode = keyof typeof API_ERRORS

/** A code's params, or `undefined` for a code that declares none. */
export type ApiErrorParams<C extends ApiErrorCode> = (typeof API_ERRORS)[C] extends { params: infer P } ? P : undefined

export type PlanLimitCode = Extract<ApiErrorCode, `plan.limit.${string}`>

export const isApiErrorCode = (value: unknown): value is ApiErrorCode =>
  typeof value === 'string' && Object.hasOwn(API_ERRORS, value)

export const isPlanLimitCode = (value: unknown): value is PlanLimitCode =>
  isApiErrorCode(value) && value.startsWith('plan.limit.')

/**
 * What the frontend falls back to when a response carries no code: a plain Nest exception, a proxy
 * error page, or no response at all. Translated like the codes above.
 */
export const HTTP_FALLBACK_CODES = [
  'http.badRequest',
  'http.unauthorized',
  'http.forbidden',
  'http.notFound',
  'http.conflict',
  'http.tooManyRequests',
  'http.serverError',
  'network.failed',
] as const

export type HttpFallbackCode = (typeof HTTP_FALLBACK_CODES)[number]

export const httpFallbackCode = (status: number): HttpFallbackCode => {
  switch (status) {
    case 0:
      return 'network.failed'
    case 401:
      return 'http.unauthorized'
    case 403:
      return 'http.forbidden'
    case 404:
      return 'http.notFound'
    case 409:
      return 'http.conflict'
    case 429:
      return 'http.tooManyRequests'
    default:
      return status >= 500 ? 'http.serverError' : 'http.badRequest'
  }
}

/**
 * One failed rule of a `validation.failed` response. The Zod issue minus its input: `code` and the
 * fields Zod's locales read (`minimum`, `origin`, `format`, …) let the frontend word a built-in
 * issue itself, and `params.key` names a Seiri rule. `message` is English.
 */
export interface ApiValidationIssue {
  path: (string | number)[]
  code: string
  message: string
  params?: { key?: ValidationKey } & Record<string, unknown>
  [field: string]: unknown
}

/** The body of every error response that carries a code. */
export interface ApiErrorBody<C extends ApiErrorCode = ApiErrorCode> {
  statusCode: number
  code: C
  /** English, for developers and logs. `string[]` (`path: message`) for `validation.failed`. */
  message: string | string[]
  params?: ApiErrorParams<C>
  /** `validation.failed` only. */
  issues?: ApiValidationIssue[]
}
